import { useEffect, useRef } from "react";

export default function Analysis({ analysis, isPlay }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const g = canvas?.getContext("2d");
    if (!g) return;
    const analyser = analysis?.analyser;
    const buffer = new Float32Array(analyser?.fftSize || 2048);
    let width = 1;
    let height = 1;
    let rafId = 0;
    let lastFrame = -Infinity;
    let displayPeak = 0;
    let displayWave = new Float32Array(1);
    const resize = () => {
      width = Math.max(1, Math.round(canvas.clientWidth));
      height = Math.max(1, Math.round(canvas.clientHeight));
      displayWave = new Float32Array(width);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();

    const draw = (now) => {
      rafId = requestAnimationFrame(draw);
      if (now - lastFrame < 1000 / 20) return;
      const elapsed = Number.isFinite(lastFrame) ? now - lastFrame : 50;
      const blend = 1 - Math.exp(-Math.min(elapsed, 100) / 250);
      lastFrame = now;
      if (analyser && isPlay) analyser.getFloatTimeDomainData(buffer);
      else buffer.fill(0);
      let peak = 0;
      for (const value of buffer) peak = Math.max(peak, Math.abs(value));
      // Fast attack / slow release keeps quiet signals visible without clipping peaks.
      displayPeak = Math.max(peak, displayPeak * 0.94);
      const gain = 1 / Math.max(displayPeak, 0.00001);
      const active = isPlay && peak > 0.000001;
      const left = 34;
      const top = 8;
      const plotWidth = Math.max(1, width - left - 8);
      const plotHeight = Math.max(1, height - top - 22);
      const center = top + plotHeight / 2;
      const amplitude = plotHeight * 0.46;
      const sampleRate = analysis?.spec?.sampleRate || analysis?.context?.sampleRate;
      const windowMs = sampleRate ? buffer.length / sampleRate * 1000 : null;
      g.clearRect(0, 0, width, height);
      g.fillStyle = "#ffffff";
      g.fillRect(0, 0, width, height);
      g.font = "10px ui-monospace, monospace";
      g.textBaseline = "middle";
      g.textAlign = "right";
      for (let tick = 0; tick <= 4; tick++) {
        const y = top + tick * plotHeight / 4;
        const value = 1 - tick * 0.5;
        g.strokeStyle = tick === 2 ? "#8aa7c9" : "#dce7f4";
        g.lineWidth = 1;
        g.beginPath(); g.moveTo(left, y); g.lineTo(left + plotWidth, y); g.stroke();
        g.fillStyle = "#59718b";
        g.fillText(value > 0 ? `+${value.toFixed(1)}` : value.toFixed(1), left - 6, y);
      }
      for (let tick = 0; tick <= 4; tick++) {
        const x = left + tick * plotWidth / 4;
        g.strokeStyle = tick === 2 ? "#8aa7c9" : "#dce7f4";
        g.beginPath(); g.moveTo(x, top); g.lineTo(x, top + plotHeight); g.stroke();
        if (tick % 2 === 0) {
          g.textAlign = tick === 0 ? "left" : tick === 4 ? "right" : "center";
          g.fillStyle = "#59718b";
          g.fillText(windowMs === null ? "—" : `${Number((windowMs * tick / 4).toFixed(1))} ms`, x, height - 7);
        }
      }
      g.save();
      g.beginPath(); g.rect(left, top, plotWidth, plotHeight); g.clip();
      g.strokeStyle = "#2474df";
      g.lineWidth = 1;
      g.beginPath();
      if (active) {
        // Retain a real signed peak per pixel, then soften changes over 250 ms.
        // This is a visual waveform only; audio and FFT measurements are untouched.
        const columns = Math.ceil(plotWidth);
        for (let x = 0; x < columns; x++) {
          const start = Math.floor(x * buffer.length / columns);
          const end = Math.min(buffer.length, Math.max(start + 1, Math.floor((x + 1) * buffer.length / columns)));
          let value = buffer[start];
          for (let i = start + 1; i < end; i++) {
            if (Math.abs(buffer[i]) > Math.abs(value)) value = buffer[i];
          }
          displayWave[x] += (value * gain - displayWave[x]) * blend;
          const y = center - displayWave[x] * amplitude;
          if (x === 0) g.moveTo(left, y);
          else g.lineTo(left + x, y);
        }
      } else {
        displayWave.fill(0);
        g.moveTo(left, center); g.lineTo(left + plotWidth, center);
      }
      g.stroke();
      g.restore();
    };
    rafId = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(rafId); observer.disconnect(); };
  }, [analysis, isPlay]);

  return (
    <div className="analysis">
      <div className="sample-wave-heading"><strong>오디오 파형</strong></div>
      <canvas ref={canvasRef} role="img" aria-label="재생 음원의 파형. 작은 신호를 보기 쉽게 진폭을 자동 확대합니다." />
    </div>
  );
}
